import React, { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormAdditionalValidation, FormFields, FormTextField, Stack } from "sprint-common-web";

import { useGpacCommandValidation } from "../useGpacCommandValidation";
import { useCreateGpacMuxSpecificationForm } from "./useCreateGpacMuxSpecificationForm";

export interface CreateFramerateProfileFormProps {
    onCancel(): void;
    onSubmit(): void;
}

export interface CreateGpacMuxSpecificationFormInputs {
    name: string;
    command: string;
    soundtracksCommand: string;
    subtitlesCommand?: string;
}

export const CreateGpacMuxSpecificationForm: FunctionComponent<CreateFramerateProfileFormProps> = (
    props: CreateFramerateProfileFormProps
) => {
    const { errors, control, formState, reset, handleSubmit, getValues } = useForm<
        CreateGpacMuxSpecificationFormInputs
    >({
        defaultValues: {
            name: "",
            command: "",
            soundtracksCommand: "",
            subtitlesCommand: ""
        },
        criteriaMode: "all"
    });

    const { createGpacMuxSpecification } = useCreateGpacMuxSpecificationForm();
    const {
        validateCommand,
        validateCommandsCorrelation,
        validateSoundtracksCommand,
        validateSubtitlesCommand
    } = useGpacCommandValidation();

    const handleCreateGpacMuxSpecification = async (inputs: CreateGpacMuxSpecificationFormInputs) => {
        await createGpacMuxSpecification(inputs);
        props.onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        props.onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleCreateGpacMuxSpecification)}>
            <Stack verticalAlign="space-between">
                <FormFields>
                    <FormAdditionalValidation
                        control={control}
                        name="additionalValidation"
                        errors={errors}
                        rules={{ validate: () => validateCommandsCorrelation(getValues()) }}
                    />
                    <FormTextField
                        label="Name"
                        control={control}
                        name="name"
                        errors={errors}
                        rules={{
                            required: "Name is required field"
                        }}
                    />
                    <FormTextField
                        label="Command"
                        control={control}
                        name="command"
                        errors={errors}
                        rules={{
                            required: "Command is required field",
                            validate: validateCommand
                        }}
                        tooltipContent={`Available tokens: {soundtracksCommand} {subtitlesCommand}`}
                    />
                    <FormTextField
                        label="Soundtracks command"
                        control={control}
                        name="soundtracksCommand"
                        errors={errors}
                        rules={{
                            required: "Soundtracks command is required field",
                            validate: validateSoundtracksCommand
                        }}
                        tooltipContent={`Available tokens: {soundtracksFilePath} {language.iso639} {language.laRg} {languageSpec}`}
                    />
                    <FormTextField
                        label="Subtitles command"
                        control={control}
                        name="subtitlesCommand"
                        errors={errors}
                        rules={{
                            validate: validateSubtitlesCommand
                        }}
                        tooltipContent={`Available tokens: {subtitlesFilePath} {language.iso639} {language.laRg} {languageSpec}`}
                    />
                </FormFields>
                <FormActions
                    isLoading={formState.isSubmitting || formState.isValidating}
                    onCancel={handleCancelButtonClick}
                />
            </Stack>
        </Form>
    );
};
